<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';

const { t } = useI18n({ useScope: 'global' });

const router = useRouter();

// ── Mode d'affichage de la liste : 'card' | 'table' ───────────────────────────
const viewMode = ref<'card' | 'table'>('card');

// ── Fetch tous les commerces (sans pagination pour avoir les stats globales) ──
const search = ref('');
const searchDebounced = ref('');
let debTimer: ReturnType<typeof setTimeout>;
watch(search, (v) => {
  clearTimeout(debTimer);
  debTimer = setTimeout(() => {
    searchDebounced.value = v.trim();
    avpPage.value = 1;
  }, 300);
});

const { data: apiResp, isPending } = useQuery({
  queryKey: ['avail-commerces'],
  queryFn: async () => {
    const r = await axiosInstance.get(
      'restaurants/search?pagination=false&sortBy=name&order=ASC',
    );
    return r.data;
  },
  staleTime: 60_000,
});

const allStores = computed<any[]>(() => {
  const d = apiResp.value as any;
  return d?.data?.data ?? d?.data ?? (Array.isArray(d) ? d : []);
});

// ── Fetch horaires pour tous les commerces en parallèle ──────────────────────
const { data: schedulesMap, isPending: schedPending } = useQuery({
  queryKey: computed(() => [
    'avail-schedules',
    allStores.value.map((s: any) => s.id).join(','),
  ]),
  queryFn: async () => {
    if (!allStores.value.length) return {} as Record<string, any[]>;
    const results = await Promise.all(
      allStores.value.map(async (s: any) => {
        try {
          const r = await axiosInstance.get(
            `restaurants/${s.id}/schedules/search?pagination=false`,
          );
          const d = r.data as any;
          const list: any[] =
            d?.data?.data ?? d?.data ?? (Array.isArray(d) ? d : []);
          return { id: s.id, schedules: list };
        } catch {
          return { id: s.id, schedules: [] };
        }
      }),
    );
    return Object.fromEntries(results.map((r) => [r.id, r.schedules]));
  },
  enabled: computed(() => allStores.value.length > 0),
  staleTime: 60_000,
});

// ── Helpers horaires ─────────────────────────────────────────────────────────
const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
const DAY_FR: Record<string, string> = {
  monday: 'Lun',
  tuesday: 'Mar',
  wednesday: 'Mer',
  thursday: 'Jeu',
  friday: 'Ven',
  saturday: 'Sam',
  sunday: 'Dim',
};

function regularSchedules(storeId: string) {
  return (schedulesMap.value?.[storeId] ?? []).filter(
    (s: any) => s.type !== 'holiday' && s.type !== 'special',
  );
}
function exceptionalSchedules(storeId: string) {
  return (schedulesMap.value?.[storeId] ?? []).filter(
    (s: any) => s.type === 'holiday' || s.type === 'special',
  );
}
function daysCount(storeId: string): number {
  const reg = regularSchedules(storeId);
  return new Set(reg.map((s: any) => s.weekday)).size;
}

function isOpenNow(storeId: string): boolean {
  const now = new Date();
  const dayName = DAYS[now.getDay() === 0 ? 6 : now.getDay() - 1];
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const reg = regularSchedules(storeId);
  return reg.some((s: any) => {
    if (s.weekday !== dayName) return false;
    const [oh, om] = (s.open ?? '00:00:00').split(':').map(Number);
    const [ch, cm] = (s.close ?? '00:00:00').split(':').map(Number);
    return nowMin >= oh * 60 + om && nowMin < ch * 60 + cm;
  });
}

function isPaused(store: any): boolean {
  return store.isActive === false || !!store.disabledAt;
}

function nextExceptional(storeId: string): string | null {
  const exc = exceptionalSchedules(storeId);
  if (!exc.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const future = exc
    .filter((s: any) => (s.day ?? '') >= today)
    .sort((a: any, b: any) => (a.day > b.day ? 1 : -1));
  if (!future.length) return null;
  const first = future[0];
  const d = new Date(first.day);
  return t('availability.closedOn', {
    date: d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }),
  });
}

// Regrouper les jours consécutifs de fermetures exceptionnelles
function exceptionalLabel(storeId: string): string | null {
  const exc = exceptionalSchedules(storeId).filter(
    (s: any) => s.type === 'holiday',
  );
  if (!exc.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const future = exc
    .filter((s: any) => (s.day ?? '') >= today)
    .sort((a: any, b: any) => (a.day > b.day ? 1 : -1));
  if (!future.length) return null;
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  if (future.length === 1)
    return t('availability.closedOn', { date: fmt(future[0].day) });
  return t('availability.closedFromTo', {
    from: fmt(future[0].day),
    to: fmt(future[future.length - 1].day),
  });
}

// ── Stats globales ────────────────────────────────────────────────────────────
const stats = computed(() => {
  const list = allStores.value;
  const loaded = !!schedulesMap.value;
  return {
    total: list.length,
    openNow: loaded
      ? list.filter((s: any) => !isPaused(s) && isOpenNow(s.id)).length
      : '…',
    paused: list.filter((s: any) => isPaused(s)).length,
    exceptional: loaded
      ? list.filter((s: any) => exceptionalSchedules(s.id).length > 0).length
      : '…',
  };
});

// ── Sort ──────────────────────────────────────────────────────────────────────
const sortKey = ref<'designation' | 'isActive' | 'status' | 'days'>(
  'designation',
);
const sortDir = ref<'asc' | 'desc'>('asc');

function toggleSort(key: typeof sortKey.value) {
  if (sortKey.value === key)
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
  avpPage.value = 1;
}
function sortIco(key: string) {
  if (sortKey.value !== key) return 'pi-sort-alt';
  return sortDir.value === 'asc' ? 'pi-sort-amount-up' : 'pi-sort-amount-down';
}

// ── Pagination ────────────────────────────────────────────────────────────────
const avpPage = ref(1);
const avpPageSize = ref(10);
const AVP_PAGE_SIZES = [5, 10, 20, 50];

// ── Liste filtrée + triée + paginée ──────────────────────────────────────────
const filtered = computed(() => {
  const q = searchDebounced.value.toLowerCase();
  let list = allStores.value.filter((s: any) => {
    if (!q) return true;
    const name = (s.designation ?? '').toLowerCase();
    const addr = (s.address ?? s.metadata?.city ?? '').toLowerCase();
    return name.includes(q) || addr.includes(q);
  });
  const loaded = !!schedulesMap.value;
  list = [...list].sort((a: any, b: any) => {
    let av: any = '',
      bv: any = '';
    if (sortKey.value === 'designation') {
      av = (a.designation ?? '').toLowerCase();
      bv = (b.designation ?? '').toLowerCase();
    } else if (sortKey.value === 'isActive') {
      av = a.isActive === false ? 0 : 1;
      bv = b.isActive === false ? 0 : 1;
    } else if (sortKey.value === 'status') {
      av = loaded ? (isPaused(a) ? 0 : isOpenNow(a.id) ? 2 : 1) : 0;
      bv = loaded ? (isPaused(b) ? 0 : isOpenNow(b.id) ? 2 : 1) : 0;
    } else if (sortKey.value === 'days') {
      av = loaded ? daysCount(a.id) : 0;
      bv = loaded ? daysCount(b.id) : 0;
    }
    if (av < bv) return sortDir.value === 'asc' ? -1 : 1;
    if (av > bv) return sortDir.value === 'asc' ? 1 : -1;
    return 0;
  });
  return list;
});

const avpTotalPages = computed(() =>
  Math.max(1, Math.ceil(filtered.value.length / avpPageSize.value)),
);
const paginated = computed(() => {
  const start = (avpPage.value - 1) * avpPageSize.value;
  return filtered.value.slice(start, start + avpPageSize.value);
});
function setAvpPage(p: number) {
  avpPage.value = Math.max(1, Math.min(p, avpTotalPages.value));
}
function onAvpPageSize(e: Event) {
  avpPageSize.value = Number((e.target as HTMLSelectElement).value);
  avpPage.value = 1;
}

// ── Initiales ─────────────────────────────────────────────────────────────────
function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2)
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
const COLORS = [
  '#F68402',
  '#8B5CF6',
  '#06B6D4',
  '#EC4899',
  '#10B981',
  '#3B82F6',
  '#EF4444',
];
function color(id: string): string {
  return COLORS[id.charCodeAt(0) % COLORS.length];
}

function logoUrl(s: any): string | null {
  return s.logo?.info?.secureUrl ?? s.logoUrl ?? null;
}
function address(s: any): string {
  return (
    s.address ??
    [s.metadata?.city, s.metadata?.country].filter(Boolean).join(', ') ??
    '—'
  );
}

function navigate(s: any) {
  router.push(`/commerces/${s.id}?tab=horaires`);
}
</script>

<template>
  <div class="avp">
    <!-- ══ Header ═══════════════════════════════════════════════════════════ -->
    <div class="avp__header">
      <div class="avp__header-text">
        <p class="avp__eyebrow">{{ t('availability.eyebrow') }}</p>
        <h1 class="avp__title">{{ t('availability.title') }}</h1>
        <p class="avp__subtitle">{{ t('availability.subtitle') }}</p>
      </div>
    </div>

    <!-- ══ Fond bleuté KPIs + table ════════════════════════════════════════ -->
    <div class="avp__content-wrap">
      <!-- ══ KPIs ═════════════════════════════════════════════════════════════ -->
      <div class="avp__kpis">
        <div class="avp__kpi">
          <span class="avp__kpi-val">{{ stats.total }}</span>
          <span class="avp__kpi-label">{{ t('availability.kpiStores') }}</span>
        </div>
        <div class="avp__kpi avp__kpi--open">
          <span class="avp__kpi-val">{{ stats.openNow }}</span>
          <span class="avp__kpi-label">{{ t('availability.kpiOpenNow') }}</span>
        </div>
        <div class="avp__kpi avp__kpi--paused">
          <span class="avp__kpi-val">{{ stats.paused }}</span>
          <span class="avp__kpi-label">{{ t('availability.kpiPaused') }}</span>
        </div>
        <div class="avp__kpi avp__kpi--exc">
          <span class="avp__kpi-val">{{ stats.exceptional }}</span>
          <span class="avp__kpi-label">{{
            t('availability.kpiExceptional')
          }}</span>
        </div>
      </div>

      <!-- ══ Toolbar ══════════════════════════════════════════════════════════ -->
      <div class="avp__toolbar">
        <div class="avp__search-wrap">
          <i class="pi pi-search avp__search-ico" />
          <input
            v-model="search"
            type="text"
            :placeholder="t('availability.searchPlaceholder')"
            class="avp__search"
          />
          <button v-if="search" class="avp__search-clear" @click="search = ''">
            <i class="pi pi-times" />
          </button>
        </div>
        <span class="avp__count">
          {{ filtered.length }}
          {{
            filtered.length > 1
              ? t('availability.stores')
              : t('availability.store')
          }}
        </span>
        <select
          class="avp__pg-size"
          :value="avpPageSize"
          @change="onAvpPageSize"
        >
          <option v-for="sz in AVP_PAGE_SIZES" :key="sz" :value="sz">
            {{ sz }} {{ t('availability.perPage') }}
          </option>
        </select>
        <div class="avp__view-toggle">
          <button
            class="avp__view-btn"
            :class="{ 'avp__view-btn--active': viewMode === 'card' }"
            :title="t('availability.viewCards')"
            @click="viewMode = 'card'"
          >
            <i class="pi pi-th-large" />
          </button>
          <button
            class="avp__view-btn"
            :class="{ 'avp__view-btn--active': viewMode === 'table' }"
            :title="t('availability.viewTable')"
            @click="viewMode = 'table'"
          >
            <i class="pi pi-list" />
          </button>
        </div>
      </div>

      <!-- ══ Skeleton carte ═══════════════════════════════════════════════════ -->
      <div v-if="isPending && viewMode === 'card'" class="avp__cards">
        <div
          v-for="i in 8"
          :key="i"
          class="avp__card-item avp__card-item--sk"
        />
      </div>

      <!-- ══ Skeleton table ═══════════════════════════════════════════════════ -->
      <div v-else-if="isPending" class="avp__table-wrap">
        <table class="avp__table">
          <thead>
            <tr>
              <th class="avp__th avp__th--name">
                {{ t('availability.colStore') }}
              </th>
              <th class="avp__th">{{ t('availability.colStatus') }}</th>
              <th class="avp__th">{{ t('availability.colActive') }}</th>
              <th class="avp__th">{{ t('availability.colDays') }}</th>
              <th class="avp__th">{{ t('availability.colExceptional') }}</th>
              <th class="avp__th avp__th--action"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="i in 8" :key="i" class="avp__tr avp__tr--sk">
              <td class="avp__td avp__td--name">
                <div class="avp__sk avp__sk--avatar" />
                <div>
                  <div
                    class="avp__sk"
                    style="
                      height: 0.875rem;
                      width: 140px;
                      margin-bottom: 0.35rem;
                    "
                  />
                  <div class="avp__sk" style="height: 0.7rem; width: 100px" />
                </div>
              </td>
              <td class="avp__td">
                <div
                  class="avp__sk"
                  style="height: 1.4rem; width: 60px; border-radius: 9999px"
                />
              </td>
              <td class="avp__td">
                <div
                  class="avp__sk"
                  style="height: 1.4rem; width: 48px; border-radius: 9999px"
                />
              </td>
              <td class="avp__td">
                <div class="avp__sk" style="height: 0.875rem; width: 28px" />
              </td>
              <td class="avp__td">
                <div class="avp__sk" style="height: 0.75rem; width: 80px" />
              </td>
              <td class="avp__td"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ══ Cards ════════════════════════════════════════════════════════════ -->
      <div v-else-if="viewMode === 'card'">
        <div v-if="!filtered.length" class="avp__empty avp__empty--card">
          <i class="pi pi-shop" />
          <span>{{ t('availability.noStore') }}</span>
        </div>
        <div v-else class="avp__cards">
          <button
            v-for="s in paginated"
            :key="s.id"
            class="avp__card-item"
            @click="navigate(s)"
          >
            <div
              class="avp__avatar"
              :style="logoUrl(s) ? '' : `background:${color(s.id)}`"
            >
              <img
                v-if="logoUrl(s)"
                :src="logoUrl(s)"
                class="avp__avatar-img"
                alt=""
                loading="lazy"
              />
              <span v-else>{{ initials(s.designation ?? '?') }}</span>
            </div>
            <span class="avp__card-item-name">{{ s.designation }}</span>
            <span class="avp__card-item-addr">{{ address(s) }}</span>

            <div class="avp__card-item-pills">
              <span v-if="schedPending" class="avp__pill avp__pill--neutral"
                >…</span
              >
              <span
                v-else-if="isPaused(s)"
                class="avp__pill avp__pill--paused"
                >{{ t('availability.paused') }}</span
              >
              <span
                v-else-if="isOpenNow(s.id)"
                class="avp__pill avp__pill--open"
                >{{ t('availability.open') }}</span
              >
              <span v-else class="avp__pill avp__pill--closed">{{
                t('common.closed')
              }}</span>

              <span
                class="avp__pill"
                :class="
                  s.isActive !== false ? 'avp__pill--active' : 'avp__pill--off'
                "
              >
                {{
                  s.isActive !== false
                    ? t('common.active')
                    : t('common.inactive')
                }}
              </span>
            </div>

            <div class="avp__card-item-days">
              <span v-if="schedPending" class="avp__muted">…</span>
              <span v-else class="avp__days-count">
                {{ daysCount(s.id) }}<span class="avp__days-denom">/7</span>
                {{ t('availability.colDaysWeek') }}
              </span>
            </div>

            <span
              v-if="!schedPending && exceptionalLabel(s.id)"
              class="avp__exc-label"
            >
              <i class="pi pi-calendar-times avp__exc-ico" />
              {{ exceptionalLabel(s.id) }}
            </span>
          </button>
        </div>
      </div>

      <!-- ══ Table ════════════════════════════════════════════════════════════ -->
      <div v-else class="avp__table-wrap">
        <table class="avp__table">
          <thead>
            <tr>
              <th class="avp__th avp__th--name">
                <button class="avp__th-sort" @click="toggleSort('designation')">
                  {{ t('availability.colStore') }}
                  <i class="pi avp__sort-ico" :class="sortIco('designation')" />
                </button>
              </th>
              <th class="avp__th">
                <button class="avp__th-sort" @click="toggleSort('status')">
                  {{ t('availability.colStatus') }}
                  <i class="pi avp__sort-ico" :class="sortIco('status')" />
                </button>
              </th>
              <th class="avp__th">
                <button class="avp__th-sort" @click="toggleSort('isActive')">
                  {{ t('availability.colActive') }}
                  <i class="pi avp__sort-ico" :class="sortIco('isActive')" />
                </button>
              </th>
              <th class="avp__th">
                <button class="avp__th-sort" @click="toggleSort('days')">
                  {{ t('availability.colDaysWeek') }}
                  <i class="pi avp__sort-ico" :class="sortIco('days')" />
                </button>
              </th>
              <th class="avp__th">{{ t('availability.colExceptional') }}</th>
              <th class="avp__th avp__th--action"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="s in paginated"
              :key="s.id"
              class="avp__tr"
              @click="navigate(s)"
            >
              <!-- Nom + adresse -->
              <td class="avp__td avp__td--name">
                <div
                  class="avp__avatar"
                  :style="logoUrl(s) ? '' : `background:${color(s.id)}`"
                >
                  <img
                    v-if="logoUrl(s)"
                    :src="logoUrl(s)"
                    class="avp__avatar-img"
                    alt=""
                  />
                  <span v-else>{{ initials(s.designation ?? '?') }}</span>
                </div>
                <div class="avp__store-info">
                  <span class="avp__store-name">{{ s.designation }}</span>
                  <span class="avp__store-addr">{{ address(s) }}</span>
                </div>
              </td>

              <!-- Ouvert / Fermé maintenant -->
              <td class="avp__td">
                <span v-if="schedPending" class="avp__pill avp__pill--neutral"
                  >…</span
                >
                <span
                  v-else-if="isPaused(s)"
                  class="avp__pill avp__pill--paused"
                  >{{ t('availability.paused') }}</span
                >
                <span
                  v-else-if="isOpenNow(s.id)"
                  class="avp__pill avp__pill--open"
                  >{{ t('availability.open') }}</span
                >
                <span v-else class="avp__pill avp__pill--closed">{{
                  t('common.closed')
                }}</span>
              </td>

              <!-- Actif -->
              <td class="avp__td">
                <span
                  class="avp__pill"
                  :class="
                    s.isActive !== false
                      ? 'avp__pill--active'
                      : 'avp__pill--off'
                  "
                >
                  {{
                    s.isActive !== false
                      ? t('common.active')
                      : t('common.inactive')
                  }}
                </span>
              </td>

              <!-- Jours / semaine -->
              <td class="avp__td avp__td--days">
                <span v-if="schedPending" class="avp__muted">…</span>
                <span v-else class="avp__days-count">
                  {{ daysCount(s.id) }}<span class="avp__days-denom">/7</span>
                </span>
              </td>

              <!-- Fermeture exceptionnelle -->
              <td class="avp__td avp__td--exc">
                <span v-if="schedPending" class="avp__muted">…</span>
                <span v-else-if="exceptionalLabel(s.id)" class="avp__exc-label">
                  <i class="pi pi-calendar-times avp__exc-ico" />
                  {{ exceptionalLabel(s.id) }}
                </span>
                <span v-else class="avp__muted">—</span>
              </td>

              <!-- Action -->
              <td class="avp__td avp__td--action" @click.stop="navigate(s)">
                <button class="avp__btn-manage">
                  {{ t('availability.manage') }} <i class="pi pi-arrow-right" />
                </button>
              </td>
            </tr>

            <!-- Vide -->
            <tr v-if="filtered.length === 0">
              <td colspan="6" class="avp__empty">
                <i class="pi pi-shop" />
                <span>{{ t('availability.noStore') }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pied + pagination (commun aux deux modes) -->
      <div v-if="!isPending && filtered.length" class="avp__table-foot">
        <span class="avp__pg-info">
          {{ (avpPage - 1) * avpPageSize + 1 }}–{{
            Math.min(avpPage * avpPageSize, filtered.length)
          }}
          {{ t('availability.paginationOf') }} {{ filtered.length }}
          {{
            filtered.length > 1
              ? t('availability.stores')
              : t('availability.store')
          }}
        </span>
        <div class="avp__pg-btns">
          <button
            class="avp__pg-btn"
            @click="setAvpPage(1)"
            :disabled="avpPage === 1"
          >
            <i class="pi pi-angle-double-left" />
          </button>
          <button
            class="avp__pg-btn"
            @click="setAvpPage(avpPage - 1)"
            :disabled="avpPage === 1"
          >
            <i class="pi pi-angle-left" />
          </button>
          <span class="avp__pg-current"
            >{{ avpPage }} / {{ avpTotalPages }}</span
          >
          <button
            class="avp__pg-btn"
            @click="setAvpPage(avpPage + 1)"
            :disabled="avpPage === avpTotalPages"
          >
            <i class="pi pi-angle-right" />
          </button>
          <button
            class="avp__pg-btn"
            @click="setAvpPage(avpTotalPages)"
            :disabled="avpPage === avpTotalPages"
          >
            <i class="pi pi-angle-double-right" />
          </button>
        </div>
      </div>
    </div>
    <!-- fin avp__content-wrap -->
  </div>
</template>

<style scoped>
/* ── Conteneur global ──────────────────────────────────────────────────── */
.avp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem 2.25rem;
  min-height: 100dvh;
  background: var(--nxt-surface-lowest, #f4f4f6);
  font-family: inherit;
}
[data-theme='dark'] .avp {
  background: #0c1421;
}

.avp__content-wrap {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  background: rgba(240, 243, 255, 0.35);
  border-radius: 2rem;
  padding: 1.5rem;
}
[data-theme='dark'] .avp__content-wrap {
  background: rgba(20, 22, 30, 0.35);
}

/* ── Header ───────────────────────────────────────────────────────────── */
.avp__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.avp__eyebrow {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--nxt-primary);
  margin: 0 0 0.35rem;
  opacity: 0.8;
}
.avp__title {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  margin: 0 0 0.3rem;
  letter-spacing: -0.02em;
  line-height: 1.2;
}
.avp__subtitle {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  margin: 0;
  line-height: 1.5;
}
.avp__badge-exploratory {
  flex-shrink: 0;
  padding: 0.3rem 0.75rem;
  background: #fff;
  color: var(--nxt-on-surface-variant);
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  align-self: flex-start;
  margin-top: 0.25rem;
}
[data-theme='dark'] .avp__badge-exploratory {
  background: var(--nxt-surface-mid, #1e2023);
  border-color: rgba(255, 255, 255, 0.1);
}

/* ── KPIs ─────────────────────────────────────────────────────────────── */
.avp__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}
@media (max-width: 768px) {
  .avp__kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}

.avp__kpi {
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  border-radius: 0.875rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.avp__kpi-val {
  font-size: 2rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  letter-spacing: -0.03em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.avp__kpi-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  opacity: 0.7;
}
.avp__kpi-val {
  color: var(--nxt-on-surface);
}
[data-theme='dark'] .avp__kpi-val {
  color: #fff;
}

/* ── Toolbar ──────────────────────────────────────────────────────────── */
.avp__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Toggle vue carte/tableau */
.avp__view-toggle {
  display: flex;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  overflow: hidden;
  flex-shrink: 0;
}
.avp__view-btn {
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  cursor: pointer;
}
.avp__view-btn--active {
  background: #0b1320;
  color: #fff;
}
[data-theme='dark'] .avp__view-btn--active {
  background: #ffd60a;
  color: #0b1320;
}

/* Cards (mode carte) */
.avp__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
}
.avp__card-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  padding: 1.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  font-family: inherit;
  text-align: center;
  transition:
    box-shadow 0.3s cubic-bezier(0.2, 1, 0.3, 1),
    transform 0.3s cubic-bezier(0.2, 1, 0.3, 1);
}
.avp__card-item:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.avp__card-item--sk {
  height: 14rem;
  cursor: default;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: avp-shimmer 1.4s infinite;
}
@keyframes avp-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.avp__card-item-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.avp__card-item-addr {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.avp__card-item-pills {
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
  justify-content: center;
}
.avp__card-item-days {
  font-size: 0.8125rem;
}
.avp__empty--card {
  grid-column: 1 / -1;
}
.avp__search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 380px;
}
.avp__search-ico {
  position: absolute;
  top: 50%;
  left: 0.875rem;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  pointer-events: none;
}
.avp__search {
  width: 100%;
  padding: 0.6rem 0.875rem 0.6rem 2.25rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-family: inherit;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.avp__search:focus {
  border-color: var(--nxt-primary);
}
.avp__search-clear {
  position: absolute;
  top: 50%;
  right: 0.625rem;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: none;
  background: var(--nxt-outline-variant, #ddd);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  color: var(--nxt-on-surface-variant);
  transition: background 0.14s;
}
.avp__search-clear:hover {
  background: var(--nxt-primary);
  color: #fff;
}
.avp__count {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  margin-left: auto;
  white-space: nowrap;
}

/* ── Table ────────────────────────────────────────────────────────────── */
.avp__table-wrap {
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  border-radius: 1rem;
  overflow: hidden;
  overflow-x: auto;
}
.avp__table {
  width: 100%;
  border-collapse: collapse;
  min-width: 680px;
}
.avp__th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
  opacity: 0.6;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  white-space: nowrap;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
}
.avp__th--name {
  width: 35%;
}
.avp__th--action {
  width: 80px;
}

.avp__tr {
  cursor: pointer;
  transition: background 0.12s;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.05));
}
.avp__tr:last-child {
  border-bottom: none;
}
.avp__tr:hover {
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.025));
}
.avp__tr:hover .avp__btn-manage {
  opacity: 1;
}
.avp__tr--sk {
  cursor: default;
}
.avp__tr--sk:hover {
  background: none;
}

.avp__td {
  padding: 0.875rem 1rem;
  vertical-align: middle;
}
.avp__td--name {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.avp__td--days {
  text-align: center;
}
.avp__td--exc {
  max-width: 240px;
}
.avp__td--action {
  text-align: right;
}

/* Avatar */
.avp__avatar {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
  overflow: hidden;
}
.avp__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.avp__store-info {
  display: flex;
  flex-direction: column;
  gap: 0.175rem;
  min-width: 0;
}
.avp__store-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.avp__store-addr {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Pills */
.avp__pill {
  display: inline-flex;
  align-items: center;
  padding: 0.225rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 700;
  white-space: nowrap;
}
.avp__pill--open {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}
.avp__pill--closed {
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-on-surface-variant);
}
.avp__pill--paused {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}
.avp__pill--neutral {
  background: rgba(0, 0, 0, 0.05);
  color: var(--nxt-on-surface-variant);
}
.avp__pill--active {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}
.avp__pill--off {
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
}

/* Jours */
.avp__days-count {
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  font-variant-numeric: tabular-nums;
}
.avp__days-denom {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  opacity: 0.5;
}

/* Exceptionnelle */
.avp__exc-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.7875rem;
  font-weight: 600;
  color: #7c3aed;
}
.avp__exc-ico {
  font-size: 0.75rem;
}
.avp__muted {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.4;
}

/* Bouton Gérer */
.avp__btn-manage {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
  opacity: 0;
  transition:
    opacity 0.12s,
    background 0.12s,
    border-color 0.12s,
    color 0.12s;
  white-space: nowrap;
}
.avp__btn-manage:hover {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
}
.avp__btn-manage .pi {
  font-size: 0.625rem;
}

/* Vide */
.avp__empty {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.avp__empty .pi {
  font-size: 1.75rem;
  opacity: 0.3;
}

/* Sort en-têtes */
.avp__th-sort {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: inherit;
  padding: 0;
  transition: opacity 0.14s;
}
.avp__th-sort:hover {
  opacity: 0.8;
}
.avp__sort-ico {
  font-size: 0.6rem;
}

/* Page size select dans toolbar */
.avp__pg-size {
  font-size: 0.75rem;
  font-family: inherit;
  padding: 0.3rem 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.375rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  cursor: pointer;
  outline: none;
}

/* Pied de table + pagination */
.avp__table-foot {
  padding: 0.625rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.775rem;
  color: var(--nxt-on-surface-variant);
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
}
.avp__pg-info {
  font-size: 0.75rem;
}
.avp__pg-btns {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.avp__pg-btn {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.375rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  background: var(--nxt-card-bg, #fff);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  color: var(--nxt-on-surface-variant);
  transition:
    border-color 0.12s,
    color 0.12s;
}
.avp__pg-btn:hover:not(:disabled) {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.avp__pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.avp__pg-current {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  padding: 0 0.25rem;
  white-space: nowrap;
}

/* Skeleton */
@keyframes avp-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.avp__sk {
  border-radius: 0.375rem;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.06) 25%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.06) 75%
  );
  background-size: 200% 100%;
  animation: avp-shimmer 1.4s infinite;
}
.avp__sk--avatar {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
}

[data-theme='dark'] .avp__sk {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.09) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
}
[data-theme='dark'] .avp__table-wrap {
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .avp__th {
  background: rgba(255, 255, 255, 0.025);
}
[data-theme='dark'] .avp__tr:hover {
  background: rgba(255, 255, 255, 0.03);
}

/* ── Corrections contraste dark mode additionnelles ──────────────────────── */

/* Badge exploratoire : fond blanc dur -> déjà couvert par override existant,
   mais le texte utilise nxt-on-surface-variant, on garde tel quel. */

/* KPI cards : suivre le style glassmorphism des autres pages */
[data-theme='dark'] .avp__kpi {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}

/* Barre de recherche : fond carte + focus/hover primary -> blanc en dark */
[data-theme='dark'] .avp__search {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .avp__search:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .avp__search-clear {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .avp__search-clear:hover {
  background: #ffd60a;
  color: #0b1320;
}

/* Table wrap : fond carte glassmorphism (bordure déjà overridée plus haut) */
[data-theme='dark'] .avp__table-wrap {
  background: rgba(20, 28, 41, 0.8);
}

/* Pills de statut : fonds/text bases sur rgba(0,0,0,...) invisibles en dark */
[data-theme='dark'] .avp__pill--closed {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .avp__pill--neutral {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .avp__pill--open {
  background: rgba(16, 185, 129, 0.16);
  color: #34d399;
}
[data-theme='dark'] .avp__pill--paused {
  background: rgba(245, 158, 11, 0.18);
  color: #fbbf24;
}
[data-theme='dark'] .avp__pill--active {
  background: rgba(59, 130, 246, 0.18);
  color: #60a5fa;
}
[data-theme='dark'] .avp__pill--off {
  background: rgba(239, 68, 68, 0.16);
  color: #f87171;
}

/* Bouton "Gérer" : ghost par défaut, override primary->blanc au hover */
[data-theme='dark'] .avp__btn-manage {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .avp__btn-manage:hover {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}

/* Libellé fermeture exceptionnelle : violet trop sombre sur fond dark */
[data-theme='dark'] .avp__exc-label {
  color: #c4b5fd;
}

/* Select page-size + boutons pagination : fond carte + hover primary->blanc */
[data-theme='dark'] .avp__pg-size,
[data-theme='dark'] .avp__pg-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .avp__pg-btn:hover:not(:disabled) {
  border-color: #ffd60a;
  color: #ffd60a;
}
[data-theme='dark'] .avp__table-foot {
  background: rgba(255, 255, 255, 0.025);
  border-top-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .avp__card-item {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
[data-theme='dark'] .avp__card-item:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}
[data-theme='dark'] .avp__card-item-name {
  color: #f1f5f9;
}

/* Skeletons avatar : fond gris clair figé -> déjà couvert par .avp__sk,
   mais avatar réel avec couleur de fallback garde son contraste (#fff sur
   couleur vive), pas de changement requis. */
</style>
